import { ActionError, Button, ChoiceRow, Field, Input, Radio, RadioGroup } from '@westhetics/ui'
import { useEffect, useId, useState } from 'react'
import { isApiError } from '@/lib/api'
import { errorMessage } from '@/lib/errors'
import type { CodeSent, EmailOption } from './api'

/**
 * Escolher o e-mail, mandar o código e confirmar (RN-PAC-52): o mesmo painel na anamnese pelo link e
 * na recepção. Os e-mails vêm mascarados; com um só, ele já vem escolhido. Os botões são
 * `type="button"`: o painel vive dentro de um formulário e não o envia.
 */

/** "Enviar outro código" libera 60 segundos depois do último pedido (o limite da API por e-mail). */
export const RESEND_AFTER_SECONDS = 60

const CODE_LENGTH_MESSAGE = 'O código tem 6 dígitos. Confira o e-mail e digite todos.'

const fieldOf = (error: unknown, key: string) =>
  isApiError(error) ? (error.fields[key] ?? null) : null

function useSecondsUntil(deadline: number | null) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (deadline === null) return
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [deadline])
  if (deadline === null) return 0
  return Math.min(RESEND_AFTER_SECONDS, Math.max(0, Math.ceil((deadline - now) / 1000)))
}

export function CodePanel({
  options,
  onSend,
  onConfirm,
  confirmLabel,
  confirmingLabel,
  codeHint,
}: {
  options: EmailOption[]
  onSend: (emailId: string) => Promise<CodeSent>
  /** Rejeita com o erro da API: o painel mostra o do campo ou a caixa de erro. */
  onConfirm: (emailId: string, code: string) => Promise<void>
  confirmLabel: string
  confirmingLabel: string
  /** A linha sob o campo do código (na recepção: "Peça à pessoa o código que chegou no e-mail."). */
  codeHint?: string
}) {
  const id = useId()
  const [emailId, setEmailId] = useState(options[0]?.id ?? '')
  const [sent, setSent] = useState<{ message: string; at: number } | null>(null)
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState<unknown>(null)
  const [code, setCode] = useState('')
  const [codeTouched, setCodeTouched] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [confirmError, setConfirmError] = useState<unknown>(null)
  const secondsToResend = useSecondsUntil(sent ? sent.at + RESEND_AFTER_SECONDS * 1000 : null)
  const chosen = options.find((option) => option.id === emailId)

  async function send() {
    if (sending || !emailId) return
    setSending(true)
    setSendError(null)
    try {
      const result = await onSend(emailId)
      setSent({ message: result.message, at: Date.now() })
      setCode('')
      setCodeTouched(false)
      setConfirmError(null)
    } catch (error) {
      setSendError(error)
    } finally {
      setSending(false)
    }
  }

  async function confirm() {
    setCodeTouched(true)
    if (code.length !== 6 || confirming) return
    setConfirming(true)
    setConfirmError(null)
    try {
      await onConfirm(emailId, code)
    } catch (error) {
      setConfirmError(error)
    } finally {
      setConfirming(false)
    }
  }

  const sendField = fieldOf(sendError, 'emailId')
  const sendBox = sendError && !sendField ? errorMessage(sendError) : null
  const codeError =
    fieldOf(confirmError, 'code') ?? (codeTouched && code.length !== 6 ? CODE_LENGTH_MESSAGE : null)
  const confirmBox =
    confirmError && !fieldOf(confirmError, 'code') ? errorMessage(confirmError) : null

  if (!sent) {
    return (
      <div className="mt-4 flex flex-col gap-4">
        {options.length > 1 ? (
          <RadioGroup
            value={emailId}
            onValueChange={setEmailId}
            aria-label="E-mail para receber o código"
            className="flex flex-col gap-2"
          >
            {options.map((option) => (
              <ChoiceRow
                key={option.id}
                htmlFor={`${id}-${option.id}`}
                control={<Radio id={`${id}-${option.id}`} value={option.id} />}
              >
                <span className="break-all">{option.masked}</span>
              </ChoiceRow>
            ))}
          </RadioGroup>
        ) : (
          <p className="font-body text-secondary text-[14px] leading-[1.6] font-light break-words">
            O código vai para <span className="text-ink">{chosen?.masked}</span>.
          </p>
        )}
        {sendField ? (
          <p role="alert" className="font-body text-plum text-[12px] leading-[1.5]">
            {sendField}
          </p>
        ) : null}
        {sendBox ? (
          <ActionError title="Não foi possível enviar o código" description={sendBox} />
        ) : null}
        <div>
          <Button
            type="button"
            variant="outline"
            loading={sending}
            loadingLabel="Enviando…"
            onClick={() => void send()}
          >
            Enviar código
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="mt-4 flex flex-col gap-4">
      <p className="font-body text-secondary text-[14px] leading-[1.6] font-light break-words">
        {sent.message} E-mail escolhido: <span className="text-ink">{chosen?.masked}</span>
      </p>
      <Field id={`${id}-code`} label="Código" error={codeError ?? undefined} hint={codeHint}>
        <Input
          value={code}
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]*"
          className="font-display tracking-[.3em]"
          onChange={(event) => {
            // O e-mail mostra "482 913": o corte vem depois de tirar o que não é dígito.
            setCode(event.target.value.replace(/\D/g, '').slice(0, 6))
            setConfirmError(null)
          }}
          onBlur={() => setCodeTouched(true)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              void confirm()
            }
          }}
        />
      </Field>
      {confirmBox ? (
        <ActionError title="Não foi possível confirmar" description={confirmBox} />
      ) : null}
      {sendBox ? (
        <ActionError title="Não foi possível enviar outro código" description={sendBox} />
      ) : null}
      <div className="flex flex-wrap gap-3">
        <Button
          type="button"
          loading={confirming}
          loadingLabel={confirmingLabel}
          onClick={() => void confirm()}
        >
          {confirmLabel}
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={secondsToResend > 0}
          loading={sending}
          loadingLabel="Enviando…"
          onClick={() => void send()}
        >
          {secondsToResend > 0
            ? `Enviar outro código em ${secondsToResend} s`
            : 'Enviar outro código'}
        </Button>
        {options.length > 1 ? (
          <Button
            type="button"
            variant="text"
            onClick={() => {
              setSent(null)
              setSendError(null)
              setConfirmError(null)
            }}
          >
            Usar outro e-mail
          </Button>
        ) : null}
      </div>
    </div>
  )
}
